import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';

import {
  EXPORTS, IMPORTS, SectionHeader, SectionWrapper,
} from 'src/enosikit/components/Chart/components/chartConstants';
import {
  extractTradeValues, getDirectionLabel, getTooltipFormatFunc,
} from 'src/enosikit/components/Chart/helpers/tooltip';
import { isOtherSubSeriesKey } from 'src/util/chartFiltering';
import {
  BUY, DATA_AGGREGATE_BY_PROPERTY, DATA_GROUP_BY_COUNTERPARTY, SELL,
} from 'src/util/constants';
import { getTradeTypeLabel } from 'src/util/i18n/constants';

/**
 * Renders the tooltip content for a trade chart.
 * @param {object} props Component props.
 * @param {object} props.data Tooltip datapack.
 * @param {string} props.unitLabel Unit display label.
 * @param {object} props.chartView Chart aggregation and grouping options.
 * @param {string} props.unit Chart unit.
 * @returns {React.ReactElement|null} Trade tooltip content.
 */
export default function TradeChartTooltip({
  data = null, unitLabel = null, chartView = null, unit = null,
}) {
  const intl = useIntl();

  if (!data || !chartView) {
    return null;
  }

  const values = extractTradeValues(data);
  const tooltipFormatFunc = getTooltipFormatFunc(unit, values);
  const { aggregateBy, groupBy } = chartView;
  const aggByProperty = aggregateBy === DATA_AGGREGATE_BY_PROPERTY;

  return (
    <>
      {[BUY, SELL].map((direction) => {
        if (!data[direction] || Object.keys(data[direction]).length === 0) {
          return null;
        }

        const tooltipLabel = direction === BUY
          ? getDirectionLabel(IMPORTS, intl) : getDirectionLabel(EXPORTS, intl);

        return (
          <SectionWrapper key={direction}>
            {!aggByProperty && (
              <SectionHeader>
                {tooltipLabel}
              </SectionHeader>
            )}

            {Object.keys(data[direction]).map((key) => {
              const datum = data[direction][key];
              if (!datum) {
                return null;
              }

              const {
                key: seriesKey, label, property, subLabel, value,
                counterPartyUser, counterPartyProperty, tradeType,
              } = datum;

              let tradeTypeLabel;
              if (isOtherSubSeriesKey(seriesKey)) {
                tradeTypeLabel = label;
              } else if (groupBy === DATA_GROUP_BY_COUNTERPARTY) {
                if (counterPartyProperty) {
                  tradeTypeLabel = counterPartyProperty.title
                    || getTradeTypeLabel(intl, tradeType);
                } else if (counterPartyUser) {
                  tradeTypeLabel = subLabel || getTradeTypeLabel(intl, tradeType);
                } else {
                  tradeTypeLabel = getTradeTypeLabel(intl, tradeType || label);
                }
              } else {
                tradeTypeLabel = getTradeTypeLabel(intl, aggByProperty ? subLabel : label);
              }

              return (
                <React.Fragment key={key}>
                  {(aggByProperty && property?.title) && (
                    <SectionHeader>
                      {`${tooltipLabel} - ${property.title}`}
                    </SectionHeader>
                  )}
                  <Row className="mb-1">
                    <Col className="pe-0">
                      <span>{tradeTypeLabel}</span>
                    </Col>
                    <Col xs="auto">
                      {tooltipFormatFunc(value, unitLabel)}
                    </Col>
                  </Row>
                </React.Fragment>
              );
            })}
          </SectionWrapper>
        );
      })}
    </>
  );
}

TradeChartTooltip.propTypes = {
  chartView: PropTypes.shape({}),
  data: PropTypes.shape({}),
  unit: PropTypes.string,
  unitLabel: PropTypes.string,
};
